import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Download, X } from 'lucide-react'

interface ImageLightboxProps {
  src: string
  alt: string
  onClose: () => void
}

/**
 * Overlay em tela cheia pra abrir uma imagem em tamanho grande a partir de
 * uma miniatura no chat — mesma `src` já resolvida pra miniatura, sem nova
 * requisição. Via `createPortal` em `document.body` pra não ficar preso ao
 * `overflow`/`z-index` da lista de mensagens (que tem scroll próprio).
 */
export function ImageLightbox({ src, alt, onClose }: ImageLightboxProps) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    // Fechar clicando no fundo é só um atalho de mouse — o teclado já tem
    // Escape (listener no useEffect acima) e o botão "Fechar" abaixo.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-6"
    >
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <a
          href={src}
          download={alt}
          onClick={(e) => e.stopPropagation()}
          title="Baixar imagem"
          aria-label="Baixar imagem"
          className="rounded-full bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
        >
          <Download className="h-5 w-5" />
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="rounded-full bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {/* Só impede o clique na própria imagem de borbulhar e fechar o
          overlay (clique no fundo fecha) — não é um controle em si. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
      />
    </div>,
    document.body,
  )
}
